<script setup>
import { ref, reactive, computed } from "vue";
import { GqlService } from "@js/services/gql";

// Form data
const formData = reactive({
	corporateId: "",
	forwarderId: "",
	firstName: "",
	lastName: "",
	email: "",
	phoneNumber: "",
	permissionConfirmation: false,
});

// Form state
const isSubmitting = ref(false);
const errors = ref({});
const formattedErrors = computed(() => {
	return Object.keys(errors.value).reduce((acc, key) => {
		const currentErrors = Array.isArray(errors.value[key])
			? errors.value[key]
			: [errors.value[key]];
		acc[key] = currentErrors.join("<br>");
		return acc;
	}, {});
});
const message = ref("");
const messageType = ref(""); // 'success' or 'error'

// Props for corporate options and forwarder ID
const props = defineProps({
	corporates: {
		type: Array,
		default: () => [],
	},
	forwarderId: {
		type: String,
		required: true,
	},
});

// Set forwarder ID from props
formData.forwarderId = props.forwarderId;

// Validation
const validateForm = () => {
	const newErrors = {};

	if (!formData.firstName.trim()) {
		newErrors.firstName = "First name is required";
	}

	if (!formData.lastName.trim()) {
		newErrors.lastName = "Last name is required";
	}

	if (!formData.email.trim()) {
		newErrors.email = "Email is required";
	} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
		newErrors.email = "Please enter a valid email address";
	}

	if (!formData.phoneNumber.trim()) {
		newErrors.phoneNumber = "Phone number is required";
	}

	if (formData.clientType === "business" && !formData.corporateId) {
		newErrors.corporateId = "Please select a company";
	}

	if (!formData.permissionConfirmation) {
		newErrors.permissionConfirmation = "Confirmation is required";
	}

	errors.value = newErrors;
	return Object.keys(newErrors).length === 0;
};

// Submit form
const handleSubmit = async () => {
	if (!validateForm()) {
		return;
	}

	isSubmitting.value = true;
	message.value = "";

	try {
		const json = await GqlService.request(
			`
				mutation CreateShipper(
					$corporateId: ID,
					$forwarderId: ID!,
					$firstName: String!,
					$lastName: String!,
					$email: String!,
					$phoneNumber: String!
				) {
					createShipper(
						corporateId: $corporateId,
						forwarderId: $forwarderId,
						firstName: $firstName,
						lastName: $lastName,
						email: $email,
						phoneNumber: $phoneNumber
					) {
						... on User {
							uid
						}
					}
				}
			`,
			{
				corporateId: formData.corporateId,
				forwarderId: formData.forwarderId,
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				phoneNumber: formData.phoneNumber,
			},
		);

		if (json.errors) {
			message.value = json.errors[0].message;
			messageType.value = "error";

			const validationErrors = json.errors[0].extensions?.validationErrors;
			if (validationErrors) errors.value = validationErrors;

			return;
		}

		const result = json.data.createShipper;

		window.location.href = `/expeditions/shippers/${result.uid}/expeditions?userCreated=true`;
	} catch (error) {
		message.value =
			error.message || "An error occurred while creating the shipper";
		messageType.value = "error";
	} finally {
		isSubmitting.value = false;
	}
};
</script>

<template>
	<div class="form-container row justify-content-center">
		<form class="col-12 col-xl-9" @submit.prevent="handleSubmit">
			<div class="section-heading text-center">
				<h2 class="section-title">Add new account</h2>
				<div class="section-text">
					<p>
						Create a new customer account. Once created, you can manage expeditions on their behalf.
					</p>
				</div>
			</div>

			<!-- Success/Error Messages -->
			<div
				v-if="message"
				:class="[
					'alert',
					messageType === 'success' ? 'alert-success' : 'alert-danger',
				]"
				role="alert"
			>
				{{ message }}
			</div>

			<!-- Corporate Selection (Business clients only) -->
			<div
				class="info-tooltip mb-4"
				data-client-type="business"
			>
				<div>
					<h5 class="text-dark-blue">
						Corporate <span class="required">*</span>
					</h5>

					<select
						v-if="corporates.length"
						v-model="formData.corporateId"
						class="form-select"
						:class="{ 'is-invalid': errors.corporateId }"
						id="corporate"
						required
					>
						<option value="">Choose a company</option>
						<option
							v-for="corporate in corporates"
							:key="corporate.id"
							:value="corporate.id"
						>
							{{ corporate.title }}
						</option>
					</select>

					<div
						v-if="errors.corporateId"
						v-html="formattedErrors.corporateId"
						class="invalid-feedback"
					></div>

					<div
						v-if="!corporates.length"
						class="empty companies-empty"
						:style="{ display: !corporates.length ? 'block' : 'none' }"
					>
						<p>There are no companies yet. Please add a company.</p>
					</div>
				</div>
			</div>

			<!-- Personal Details -->
			<h5 class="text-dark-blue">Personal details</h5>
			<div class="row justify-content-end mb-4">
				<div class="col-12 col-lg-6">
					<div class="mb-3">
						<label for="firstName" class="form-label">
							First Name <span class="required">*</span>
						</label>
						<input
							v-model="formData.firstName"
							type="text"
							class="form-control"
							:class="{ 'is-invalid': errors.firstName }"
							id="firstName"
							autocomplete="given-name"
							required
						/>
						<div v-if="errors.firstName" class="invalid-feedback">
							{{ errors.firstName }}
						</div>
					</div>
				</div>
				<div class="col-12 col-lg-6">
					<div class="mb-3">
						<label for="lastName" class="form-label">
							Last Name <span class="required">*</span>
						</label>
						<input
							v-model="formData.lastName"
							type="text"
							class="form-control"
							:class="{ 'is-invalid': errors.lastName }"
							id="lastName"
							autocomplete="family-name"
							required
						/>
						<div
							v-if="errors.lastName"
							v-html="formattedErrors.lastName"
							class="invalid-feedback"
						></div>
					</div>
				</div>
				<div class="col-12 col-lg-6">
					<div class="mb-3">
						<label for="phoneNumber" class="form-label">
							Phone number <span class="required">*</span>
						</label>
						<input
							v-model="formData.phoneNumber"
							type="tel"
							class="form-control"
							:class="{ 'is-invalid': errors.phoneNumber }"
							id="phoneNumber"
							autocomplete="tel"
							required
						/>
						<div
							v-if="errors.phoneNumber"
							v-html="formattedErrors.phoneNumber"
							class="invalid-feedback"
						></div>
					</div>
				</div>
				<div class="col-12 col-lg-6">
					<div class="mb-3">
						<label for="email" class="form-label">
							Email address <span class="required">*</span>
						</label>
						<input
							v-model="formData.email"
							type="email"
							class="form-control"
							:class="{ 'is-invalid': errors.email }"
							id="email"
							autocomplete="email"
							required
						/>
						<div
							v-if="errors.email"
							v-html="formattedErrors.email"
							class="invalid-feedback"
						></div>
					</div>
				</div>
				<div class="col-12">
					<div class="form-check">
						<input
							class="form-check-input"
							:class="{ 'is-invalid': errors.permissionConfirmation }"
							type="checkbox"
							v-model="formData.permissionConfirmation"
							id="tncAccepted"
							required
						/>
						<label class="form-label" for="tncAccepted">
							I confirm that my customer has given written permission to share
							his data with Bolster Insurance.
							<span class="required">*</span>
						</label>
						<div
							v-if="errors.permissionConfirmation"
							v-html="formattedErrors.permissionConfirmation"
							class="invalid-feedback"
						></div>
					</div>
				</div>
			</div>

			<!-- Action Buttons -->
			<div class="row justify-content-between">
				<div class="col-auto">
					<a
						href="/expeditions/shippers"
						class="btn btn-rounded btn-outline-blue"
					>
						<i class="ri-arrow-left-line" aria-hidden="true"></i>
						<span>Back</span>
					</a>
				</div>
				<div class="col-auto">
					<button
						class="btn btn-rounded btn-blue"
						type="submit"
						:disabled="isSubmitting"
					>
						<span v-if="isSubmitting">Creating...</span>
						<span v-else>Create account</span>
						<i class="ri-arrow-right-line" aria-hidden="true"></i>
					</button>
				</div>
			</div>
		</form>
	</div>
</template>

<style scoped>
.required {
	color: #dc3545;
}

.alert {
	margin-bottom: 1rem;
}

.is-invalid {
	border-color: #dc3545;
}

.invalid-feedback {
	display: block;
	width: 100%;
	margin-top: 0.25rem;
	font-size: 0.875em;
	color: #dc3545;
}

.companies-empty {
	margin-top: 0.5rem;
	padding: 1rem;
	background-color: #f8f9fa;
	border-radius: 0.375rem;
	border: 1px solid #dee2e6;
}

.btn:disabled {
	opacity: 0.65;
	cursor: not-allowed;
}
</style>
